import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import dayjs from 'dayjs'
import { useFetchScheduleDayQuery } from 'features/Schedule/api/schedule.api'
import { scheduleGridQueryOptions } from 'features/Schedule/api/scheduleQueryOptions'
import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'
import { useMemo } from 'react'

const WEEK_LENGTH = 7

export const weekDatesFrom = (anchorDate: string | null | undefined): string[] => {
  if (!anchorDate) return []
  const start = dayjs(anchorDate).startOf('week')
  return Array.from({ length: WEEK_LENGTH }, (_, index) =>
    start.add(index, 'day').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
  )
}

const useDayAvailability = (siteId: string | undefined, date: string | undefined, skip: boolean) =>
  useFetchScheduleDayQuery(
    { siteId: siteId ?? '', date: date ?? '' },
    { skip: skip || !siteId || !date, ...scheduleGridQueryOptions },
  )

export const useWeekDayAvailability = (
  siteId: string | undefined,
  anchorDate: string | null | undefined,
  enabled: boolean,
): Map<string, DoctorAvailabilityGroup[]> => {
  const dates = useMemo(() => weekDatesFrom(anchorDate), [anchorDate])
  const skip = !enabled

  const day0 = useDayAvailability(siteId, dates[0], skip)
  const day1 = useDayAvailability(siteId, dates[1], skip)
  const day2 = useDayAvailability(siteId, dates[2], skip)
  const day3 = useDayAvailability(siteId, dates[3], skip)
  const day4 = useDayAvailability(siteId, dates[4], skip)
  const day5 = useDayAvailability(siteId, dates[5], skip)
  const day6 = useDayAvailability(siteId, dates[6], skip)

  return useMemo(() => {
    const byDate = new Map<string, DoctorAvailabilityGroup[]>()
    if (!enabled) return byDate
    const loaded = [day0.data, day1.data, day2.data, day3.data, day4.data, day5.data, day6.data]
    dates.forEach((date, index) => {
      const data = loaded[index]
      if (!data) return
      byDate.set(date, data.availability ?? [])
    })
    return byDate
  }, [enabled, dates, day0.data, day1.data, day2.data, day3.data, day4.data, day5.data, day6.data])
}
